import { useCallback, useEffect, useState } from "react";
import Dashboard from "../../common/components/layout/Dashboard";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import { FaMoneyCheckAlt, FaTools, FaMotorcycle } from "react-icons/fa";
import { BsSignStopLightsFill } from "react-icons/bs";
import { MdWebhook } from "react-icons/md";
import { IconType } from "react-icons";
import { getMonitoringStatus, MonitoringItemStatus } from "./monitoring.api";

interface MonitoringCardProps {
  title: string;
  icon: IconType;
  status: "ok" | "warning" | "error";
  lastEventAt: string | null;
  loading?: boolean;
}

function formatLastEvent(dateStr: string | null): string {
  if (!dateStr) return "Sem registros";
  const date = new Date(dateStr);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }: { status: MonitoringCardProps["status"] }) {
  const config = {
    ok: { label: "Operacional", bg: "bg-green-500/20", text: "text-green-400", dot: "bg-green-400" },
    warning: { label: "Atenção", bg: "bg-yellow-500/20", text: "text-yellow-400", dot: "bg-yellow-400" },
    error: { label: "Falha", bg: "bg-red-500/20", text: "text-red-400", dot: "bg-red-400" },
  }[status];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <span className={`w-2 h-2 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}

function MonitoringCard({ title, icon: Icon, status, lastEventAt, loading }: MonitoringCardProps) {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-3">
          <Icon className="text-xl text-zinc-400" />
          <CardTitle className="text-sm text-zinc-200">{title}</CardTitle>
        </div>
        {loading ? (
          <span className="text-xs text-zinc-500">Carregando...</span>
        ) : (
          <StatusBadge status={status} />
        )}
      </CardHeader>
      <CardContent>
        <p className="text-xs text-zinc-500">
          {loading ? "—" : `Último evento: ${formatLastEvent(lastEventAt)}`}
        </p>
      </CardContent>
    </Card>
  );
}

const EMPTY_ITEM: MonitoringItemStatus = { status: "ok", lastEventAt: null };

export default function MonitoringPanelPage() {
  const [loading, setLoading] = useState(true);
  const [charges, setCharges] = useState({
    preparations: EMPTY_ITEM,
    maintenances: EMPTY_ITEM,
    trafficFines: EMPTY_ITEM,
  });
  const [webhooks, setWebhooks] = useState({
    charges: EMPTY_ITEM,
    payments: EMPTY_ITEM,
    trafficFines: EMPTY_ITEM,
  });

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMonitoringStatus();
      if (result.ok && result.data) {
        setCharges(result.data.charges);
        setWebhooks(result.data.webhooks);
      }
    } catch (err) {
      console.error("Erro ao buscar status do monitoramento:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return (
    <Dashboard>
      <div className="flex flex-col gap-8 p-8">
        <h1 className="text-2xl font-bold text-white">
          Painel de Monitoramento
        </h1>

        {/* Cobranças */}
        <section>
          <h2 className="text-lg font-semibold text-zinc-300 mb-4 flex items-center gap-2">
            <FaMoneyCheckAlt className="text-zinc-400" />
            Cobranças
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MonitoringCard title="Preparações" icon={FaMotorcycle} loading={loading} {...charges.preparations} />
            <MonitoringCard title="Manutenções" icon={FaTools} loading={loading} {...charges.maintenances} />
            <MonitoringCard title="Gestão de Multas" icon={BsSignStopLightsFill} loading={loading} {...charges.trafficFines} />
          </div>
        </section>

        {/* Webhooks */}
        <section>
          <h2 className="text-lg font-semibold text-zinc-300 mb-4 flex items-center gap-2">
            <MdWebhook className="text-zinc-400" />
            Webhooks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MonitoringCard title="Cobranças" icon={MdWebhook} loading={loading} {...webhooks.charges} />
            <MonitoringCard title="Recebimentos" icon={MdWebhook} loading={loading} {...webhooks.payments} />
            <MonitoringCard title="Multas" icon={MdWebhook} loading={loading} {...webhooks.trafficFines} />
          </div>
        </section>
      </div>
    </Dashboard>
  );
}
